/**
 * <mouse-parallax> subtly drifts its first element child as the cursor
 * moves — a classic mouse-parallax effect. The element itself uses
 * `display: contents` so it never affects layout; the transform is applied
 * to the wrapped child via the `translate` property, which composes with
 * (rather than overwrites) any existing CSS transforms.
 *
 * Attributes:
 * - `data-strength` — max drift in px at the viewport edges (default 12)
 * - `data-invert`   — drift away from the cursor instead of toward it,
 *                     which reads as "deeper" than the page
 *
 * Motion is eased with a lerp on requestAnimationFrame, so the text drifts
 * rather than snaps. The whole effect is skipped for coarse pointers
 * (touch) and for visitors who prefer reduced motion.
 */

/** Lerp factor per frame — lower is floatier. */
const EASE = 0.08;

class MouseParallax extends HTMLElement {
  #raf = 0;
  #x = 0;
  #y = 0;
  #targetX = 0;
  #targetY = 0;

  connectedCallback() {
    const motionOk =
      window.matchMedia('(prefers-reduced-motion: no-preference)').matches &&
      window.matchMedia('(pointer: fine)').matches;
    if (!motionOk) return;

    window.addEventListener('pointermove', this.#onPointerMove, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('pointermove', this.#onPointerMove);
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const strength = (Number(this.dataset.strength) || 12) * (this.hasAttribute('data-invert') ? -1 : 1);

    // Cursor position normalized to -1..1 from the viewport center.
    this.#targetX = ((event.clientX / window.innerWidth) * 2 - 1) * strength;
    this.#targetY = ((event.clientY / window.innerHeight) * 2 - 1) * strength;

    if (!this.#raf) this.#raf = requestAnimationFrame(this.#tick);
  };

  #tick = () => {
    this.#x += (this.#targetX - this.#x) * EASE;
    this.#y += (this.#targetY - this.#y) * EASE;

    const target = this.firstElementChild;
    if (target instanceof HTMLElement) {
      target.style.translate = `${this.#x.toFixed(2)}px ${this.#y.toFixed(2)}px`;
    }

    const settled = Math.abs(this.#targetX - this.#x) < 0.05 && Math.abs(this.#targetY - this.#y) < 0.05;
    this.#raf = settled ? 0 : requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('mouse-parallax')) {
  customElements.define('mouse-parallax', MouseParallax);
}
